import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { LTBreadcrumbs, LTButton, LTTooltip } from '@lambdatestincprivate/lt-components';
import { closePack, downloadPack, openIssueDrawer, usePackState } from '../data/pack-store';
import { openPackFromInput } from '../app/open-pack';
import { useViewerMode } from '../app/mode';
import { DownloadIcon } from '@primer/octicons-react';
import { applyDeepLink, closeDetailInPlace, closeDetailWithUrl, listenForTestParam, openDetailWithUrl, syncViewParams, writeTabParam } from '../app/url-sync';
import { TestDetailMount } from './Detail/TestDetailPage';
import { EV_DOWNLOAD_PACK_EVENT, EV_OPEN_PALETTE_EVENT } from '../app/host-events';
import { CommandPalette, PALETTE_SHORTCUT_LABEL } from './Palette/CommandPalette';
import { SearchGlyph } from './icons/SearchGlyph';
import { truncateMiddle } from './format';
import { InstanceList } from './Instances/InstanceList';
import { Landing } from './Landing/Landing';
import { RunHeader } from './RunHeader/RunHeader';
import { NeedAttention } from './Report/NeedAttention';
import { SummaryStrip } from './Summary/SummaryStrip';
import { RunTabs } from './Tabs/RunTabs';
import { ErrorScreen } from './states/ErrorScreen';
import { LoadingJourney } from './states/LoadingJourney';
import { CoverageTab } from './Coverage/CoverageTab';
import { IssuesTab } from './Issues/IssuesTab';
import { RcaDrawer } from './Issues/RcaDrawer';
import styles from './app-shell.module.css';

// Lazy: the internal explorer must be BUNDLE-inert for prod users, not
// just behavior-inert — the chunk only loads when the flag-gated tab
// actually renders.
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var ExplorerTab = /*#__PURE__*/React.lazy(function () {
  return import('./Explorer/ExplorerTab').then(function (m) {
    return {
      "default": m.ExplorerTab
    };
  });
});

/** Loading spinner, shown only when opening takes longer than a flash. */
function OpeningScreen() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    visible = _useState2[0],
    setVisible = _useState2[1];
  useEffect(function () {
    var t = setTimeout(function () {
      return setVisible(true);
    }, 150);
    return function () {
      return clearTimeout(t);
    };
  }, []);
  if (!visible) return null;
  return /*#__PURE__*/_jsx(LoadingJourney, {
    stage: "Opening evidence pack\u2026",
    progress: 0.45,
    variant: "page"
  });
}

/**
 * Slim top bar — STANDALONE viewer only (the TMS mount has the host's own
 * header; its search trigger lives there, see EV_OPEN_PALETTE_EVENT).
 * Left: "Evidence" plus the run title as a breadcrumb path when the pack
 * authors one; right: palette search, then download the .evidence file.
 */
function TopBar(_ref) {
  var title = _ref.title,
    onOpenSearch = _ref.onOpenSearch;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.topBar,
    children: [/*#__PURE__*/_jsxs("span", {
      className: styles.topBarPath,
      children: [/*#__PURE__*/_jsx("span", {
        className: title ? styles.topBarCrumb : styles.topBarCurrent,
        children: "Evidence"
      }), title && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.topBarSep,
          "aria-hidden": "true",
          children: "/"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.topBarCurrent,
          title: title,
          children: title
        })]
      })]
    }), /*#__PURE__*/_jsxs("span", {
      className: styles.topBarActions,
      children: [/*#__PURE__*/_jsx(LTButton, {
        variant: LTButton.Variant.DEFAULT,
        size: LTButton.Size.SMALL,
        label: "Search",
        ariaLabel: "Search tests, tabs, actions (".concat(PALETTE_SHORTCUT_LABEL, ")"),
        leadingIcon: /*#__PURE__*/_jsx(SearchGlyph, {}),
        trailingIcon: /*#__PURE__*/_jsx("span", {
          className: styles.topBarShortcut,
          children: PALETTE_SHORTCUT_LABEL
        }),
        onClick: onOpenSearch
      }), /*#__PURE__*/_jsx(LTButton, {
        variant: LTButton.Variant.DEFAULT,
        size: LTButton.Size.SMALL,
        label: ".evidence",
        ariaLabel: "Download the .evidence file",
        leadingIcon: /*#__PURE__*/_jsx(DownloadIcon, {
          size: 16,
          "aria-hidden": "true"
        }),
        onClick: function onClick() {
          return downloadPack();
        }
      })]
    })]
  });
}
function OpenPack(_ref2) {
  var _header$title;
  var reportExtras = _ref2.reportExtras;
  var mode = useViewerMode();
  var header = usePackState(function (s) {
    return s.header;
  });
  var stats = usePackState(function (s) {
    return s.stats;
  });
  var detailOpen = usePackState(function (s) {
    return s.detail !== null;
  });
  var detailTitle = usePackState(function (s) {
    var _s$detail$header$titl, _s$detail;
    return (_s$detail$header$titl = (_s$detail = s.detail) === null || _s$detail === void 0 ? void 0 : _s$detail.header.title) !== null && _s$detail$header$titl !== void 0 ? _s$detail$header$titl : null;
  });
  var detailTc = usePackState(function (s) {
    var _s$detail$header$test, _s$detail2;
    return (_s$detail$header$test = (_s$detail2 = s.detail) === null || _s$detail2 === void 0 ? void 0 : _s$detail2.header.testcaseId) !== null && _s$detail$header$test !== void 0 ? _s$detail$header$test : null;
  });
  var issueCount = usePackState(function (s) {
    var _s$attention$totals$f, _s$attention;
    return (_s$attention$totals$f = (_s$attention = s.attention) === null || _s$attention === void 0 ? void 0 : _s$attention.totals.failures) !== null && _s$attention$totals$f !== void 0 ? _s$attention$totals$f : 0;
  });
  var scrollRef = useRef(null);
  // The scroll element ALSO lives in state: the virtualizer needs a
  // re-render once it exists (see InstanceList.scrollElement).
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    scrollEl = _useState4[0],
    setScrollEl = _useState4[1];
  var attachScroll = useCallback(function (el) {
    scrollRef.current = el;
    setScrollEl(el);
  }, []);
  // Mirrors the TMS run page: Report | Test Instances | Issues. Report is
  // the landing tab; the list renders only on its own tab. Hydration is
  // store-driven, so results keep loading while the user reads the report.
  var _useState5 = useState('report'),
    _useState6 = _slicedToArray(_useState5, 2),
    tab = _useState6[0],
    setTab = _useState6[1];
  // Search state lives here (not in InstanceList): the pane unmounts while
  // a detail view is open and the query must survive the round trip.
  var _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    instanceQuery = _useState8[0],
    setInstanceQuery = _useState8[1];
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    paletteOpen = _useState0[0],
    setPaletteOpen = _useState0[1];
  // Where the user was in the list when they opened a detail.
  var snapshotRef = useRef(null);
  var wasDetailOpen = useRef(false);
  var handleSelect = useCallback(function (id) {
    var _scrollRef$current$sc, _scrollRef$current;
    snapshotRef.current = {
      scrollTop: (_scrollRef$current$sc = (_scrollRef$current = scrollRef.current) === null || _scrollRef$current === void 0 ? void 0 : _scrollRef$current.scrollTop) !== null && _scrollRef$current$sc !== void 0 ? _scrollRef$current$sc : 0,
      rowId: id
    };
    openDetailWithUrl(id);
  }, []);

  // Tab changes flow through here so the ?tab= param stays in step (the
  // URL is the shareable record of the whole view).
  var goTab = useCallback(function (t) {
    setTab(t);
    writeTabParam(t);
  }, []);

  // Deep link replay + the store→URL mirror. applyDeepLink waits out
  // hydration (?test needs the row index, ?issue the attention index) and
  // opens the exact view; syncViewParams then keeps ?step/?dock/?issue
  // mirrored for the mount's lifetime. Browser Back/Forward re-derive the
  // view through the popstate listener.
  useEffect(function () {
    var cancelReplay = applyDeepLink(goTab);
    var stopSync = syncViewParams();
    var unlisten = listenForTestParam(goTab);
    return function () {
      cancelReplay();
      stopSync();
      unlisten();
    };
  }, [goTab]);

  // ⌘K / Ctrl+K opens the command palette anywhere on the open pack (run
  // view AND detail — the listener lives above both). The window event is
  // the host-facing entry point (the TMS header's Search button).
  useEffect(function () {
    var onKey = function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(function (v) {
          return !v;
        });
      }
    };
    var onOpenEvent = function onOpenEvent() {
      return setPaletteOpen(true);
    };
    var onDownloadEvent = function onDownloadEvent() {
      return downloadPack();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(EV_OPEN_PALETTE_EVENT, onOpenEvent);
    window.addEventListener(EV_DOWNLOAD_PACK_EVENT, onDownloadEvent);
    return function () {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(EV_OPEN_PALETTE_EVENT, onOpenEvent);
      window.removeEventListener(EV_DOWNLOAD_PACK_EVENT, onDownloadEvent);
    };
  }, []);

  // Returning from a detail: restore the list scroll offset and put focus
  // back on the row that was opened.
  useEffect(function () {
    if (wasDetailOpen.current && !detailOpen) {
      var snap = snapshotRef.current;
      if (snap) {
        requestAnimationFrame(function () {
          var _document$getElementB;
          if (scrollRef.current) scrollRef.current.scrollTop = snap.scrollTop;
          (_document$getElementB = document.getElementById("ev-instance-row-".concat(snap.rowId))) === null || _document$getElementB === void 0 || _document$getElementB.focus();
        });
      }
    }
    wasDetailOpen.current = detailOpen;
  }, [detailOpen]);
  if (!header) return null;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.page,
    children: [(stats === null || stats === void 0 ? void 0 : stats.degraded) && /*#__PURE__*/_jsx("div", {
      className: "ev-t-small ".concat(styles.degraded),
      role: "status",
      children: "This pack's host doesn't support Range requests \u2014 the whole file was downloaded once instead of streamed."
    }), /*#__PURE__*/_jsx(CommandPalette, {
      open: paletteOpen,
      onClose: function onClose() {
        return setPaletteOpen(false);
      },
      goTab: goTab
    }), mode !== 'edit' && /*#__PURE__*/_jsx(TopBar, {
      title: (_header$title = header.title) !== null && _header$title !== void 0 ? _header$title : null,
      onOpenSearch: function onOpenSearch() {
        return setPaletteOpen(true);
      }
    }), /*#__PURE__*/_jsx(RunTabs, {
      active: detailOpen ? 'instances' : tab,
      onChange: function onChange(t) {
        // In place, not history.back(): the async popstate would
        // re-derive the OLD entry's ?tab and clobber this switch.
        if (detailOpen) closeDetailInPlace();
        goTab(t);
      },
      instanceCount: header.totals.tests,
      issueCount: issueCount
    }), detailOpen ?
    /*#__PURE__*/
    // Full-page detail below the tabs; the breadcrumb marks the drill-in.
    // Back / Escape / browser Back / breadcrumb all return to the list
    // with scroll + search intact.
    _jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("nav", {
        className: styles.crumbBar,
        "aria-label": "You are here",
        children: /*#__PURE__*/_jsx(LTTooltip, {
          text: detailTc ? "".concat(detailTc, " \xB7 ").concat(detailTitle !== null && detailTitle !== void 0 ? detailTitle : '') : detailTitle !== null && detailTitle !== void 0 ? detailTitle : '',
          direction: "bottom",
          customTriggerStyle: {
            display: 'inline-flex',
            minWidth: 0
          },
          children: /*#__PURE__*/_jsx(LTBreadcrumbs, {
            items: [{
              key: 'instances',
              label: 'Test Instances',
              selected: false,
              onClick: function onClick() {
                return closeDetailWithUrl();
              }
            }, {
              key: 'current',
              label: detailTc ? "".concat(truncateMiddle(detailTc, 15), " \xB7 ").concat(detailTitle !== null && detailTitle !== void 0 ? detailTitle : '') : detailTitle !== null && detailTitle !== void 0 ? detailTitle : '',
              selected: true
            }]
          })
        })
      }), /*#__PURE__*/_jsx(TestDetailMount, {})]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [tab === 'report' && /*#__PURE__*/_jsxs("div", {
        className: styles.reportPane,
        children: [/*#__PURE__*/_jsx(RunHeader, {
          run: header
        }), /*#__PURE__*/_jsx(SummaryStrip, {
          run: header
        }), /*#__PURE__*/_jsx(NeedAttention, {
          onOpenIssue: function onOpenIssue(item) {
            goTab('issues');
            openIssueDrawer(item);
          }
        }), reportExtras]
      }), tab === 'instances' && /*#__PURE__*/_jsx("main", {
        ref: attachScroll,
        className: styles.scroll,
        children: /*#__PURE__*/_jsx(InstanceList, {
          scrollElement: scrollEl,
          query: instanceQuery,
          onQueryChange: setInstanceQuery,
          onSelect: handleSelect
        })
      }), tab === 'issues' && /*#__PURE__*/_jsx(IssuesTab, {}), tab === 'coverage' && /*#__PURE__*/_jsx(CoverageTab, {}), tab === 'explorer' && /*#__PURE__*/_jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/_jsx(ExplorerTab, {})
      })]
    }), /*#__PURE__*/_jsx(RcaDrawerMount, {})]
  });
}
function RcaDrawerMount() {
  var drawerOpen = usePackState(function (s) {
    return s.issueDrawer !== null;
  });
  return drawerOpen ? /*#__PURE__*/_jsx(RcaDrawer, {}) : null;
}

/**
 * One PERSISTENT live region: screen readers only announce content that
 * changes after the region is mounted, so it must exist across every phase
 * and have its text swapped — a region mounted together with its text never
 * announces anything.
 */
function LiveStatus() {
  var phase = usePackState(function (s) {
    return s.phase;
  });
  var header = usePackState(function (s) {
    return s.header;
  });
  var progress = usePackState(function (s) {
    return s.progress;
  });
  var message = '';
  if (phase === 'loading') {
    message = 'Opening evidence pack';
  } else if (phase === 'open' && header) {
    var hydrating = progress.done + progress.failed < progress.total;
    message = hydrating ? "Run loaded: ".concat(header.title, ", ").concat(header.totals.tests, " tests. Loading test details.") : "Run loaded: ".concat(header.title, ", ").concat(header.totals.tests, " tests.");
  }
  return /*#__PURE__*/_jsx("span", {
    className: "ev-sr-only",
    "aria-live": "polite",
    children: message
  });
}
export function AppShell(_ref3) {
  var reportExtras = _ref3.reportExtras;
  var phase = usePackState(function (s) {
    return s.phase;
  });
  var error = usePackState(function (s) {
    return s.error;
  });
  var content;
  if (phase === 'landing') {
    content = /*#__PURE__*/_jsx(Landing, {
      onFile: function onFile(f) {
        return openPackFromInput(f);
      },
      onUrl: function onUrl(u) {
        return openPackFromInput(u);
      }
    });
  } else if (phase === 'loading') {
    content = /*#__PURE__*/_jsx(OpeningScreen, {});
  } else if (phase === 'error' && error) {
    content = /*#__PURE__*/_jsx(ErrorScreen, {
      error: error,
      onReset: function onReset() {
        return void closePack();
      }
    });
  } else {
    content = /*#__PURE__*/_jsx(OpenPack, {
      reportExtras: reportExtras
    });
  }
  return (
    /*#__PURE__*/
    // Scope class for every packaged style — prevents global bleed when the
    // component mounts inside a host app (kaneai's /evidence route).
    _jsxs("div", {
      className: "ltEvidenceViewer",
      children: [/*#__PURE__*/_jsx(LiveStatus, {}), content]
    })
  );
}